import {ExtraProps, IReportPresenter, ReportPresenterEvents} from "./IReportPresenter";
import {IReportService} from "../../features-api/report_service";
import {default as AreaReport} from "@manaakiwhenua/oe-report-elements/src/components/AreaReport.svelte";
import {default as PinReport} from "@manaakiwhenua/oe-report-elements/src/components/PinReport.svelte";
import {default as ErrorReport} from "@manaakiwhenua/oe-report-elements/src/components/ErrorReport.svelte";
import {ILoginService} from "../login-service";
import {LTServices} from "../../../lt-ng/lt-services";
import IMapService = LTServices.IMapService;
import {proj4} from "../../../ol/register-proj";
import {_F} from "../../../utility/func";
import once = _F.once;
import {FragReport, UnsavedFragReport} from "./IFragReport";
import {UserFeature} from "../../features-api/models";

export enum ReportTypes {
    pinReport = '81b80dfc-e52e-4878-aeb2-7329bde82b3d',
    areaReport = '9db0ea6b-a660-46c8-9e66-b6ff77eb2b22'
}

export class FragSvelteReportPresenter implements IReportPresenter {
    public static $inject = ['reportService', '$rootScope', 'loginService', 'landcareMapService'];
    private _reportContainer: HTMLElement;

    constructor(protected reportService: IReportService,
                protected $rootScope: ng.IRootScopeService,
                protected loginService: ILoginService,
                protected mapService: IMapService) {
        window.addEventListener('keyup', (ev) => {
            if (ev.key === 'Escape' && this.closeReport) {
                this.closeReport();
            }
        })
    }

    protected closeReport: () => void;

    protected get reportContainer(): HTMLElement {
        this._reportContainer = this._reportContainer || document.getElementById('report-container');
        if (!this._reportContainer) {
            document.createElement('div');
            this._reportContainer.setAttribute('id', 'report-container');
            document.querySelector('body').appendChild(this._reportContainer);
        } else {
            this._reportContainer.innerHTML = '';
        }

        this._reportContainer.className = '';

        return this._reportContainer;
    }

    protected get reportTarget(): HTMLElement {
        const container = this.reportContainer;
        const target = document.createElement('div');
        target.classList.add('fullscreen-report');
        container.appendChild(target);
        return target;
    }

    showReportById(id: string, requestEdit: boolean) {
        this.reportService.getReport(id)
            .then((resp: ng.IHttpResponse<any>) => {
                const report = <any> resp.data;
                this.showReport(report, {editing: requestEdit})
            }, error => {
                const messages = {
                    403: "You don't have permission to view this report.",
                    404: "404 report not found"
                };

                this.showReport({}, {
                    errorMessage: error && error.status && messages[error.status] || undefined
                });
            });
    }

    showReport(report: FragReport<any> | {}, extraProps: ExtraProps = {}) {
        let componentAndTarget;
        const templateId = 'reportTemplateId' in report ? report.reportTemplateId : undefined;
        switch (templateId) {
            case ReportTypes.areaReport:
                componentAndTarget = this.showAreaReport(report, extraProps);
                break;
            case ReportTypes.pinReport:
                componentAndTarget = this.showPinReport(report, extraProps);
                break;
            default:
                componentAndTarget = this.showError(new Error("unknown report type"), extraProps);
        }

        document.querySelector('body').classList.add('report-active');

        this.closeReport = once(() => {
            console.log('closeReport');
            document.querySelector('body').classList.remove('report-active');
            componentAndTarget.component.$destroy();
            this.reportContainer.setAttribute('class', 'ng-hide');
            this.$rootScope.$broadcast(ReportPresenterEvents.closeReport);
            this.mapService.keyboardPan.setActive(true);
        });

        this.mapService.mapPromise.then(() => {
            this.mapService.keyboardPan.setActive(false);
            componentAndTarget.target.addEventListener('closeReport', () => this.closeReport(), false);
        });
    }

    showReportForFeature(feature: UserFeature, requestEdit: boolean) {
        const report: UnsavedFragReport<UserFeature> = {
            reportTemplateId: feature.geometryType === 'Point' ? ReportTypes.pinReport : ReportTypes.areaReport,
            subject: feature,
            title: feature.title,
            topics: {

            }
        };

        this.showReport(report, {});
    }

    protected reportLink(report) {
        const url = new URL(window.location.pathname, window.location.href);

        url.searchParams.set('savedReport', report.id);

        return ''+url;
    }

    showError(err: any, extraProps: ExtraProps = {}) {
        console.warn(err);
        const target = this.reportTarget;
        return {
            target,
            component: new ErrorReport({
                target,
                props: {
                    ...extraProps,
                    errorMessage: extraProps.errorMessage || "Error fetching report",
                }
            })
        }
    }

    showPinReport(report: any, extraProps = {}): any {
        const domain = window['LTConfiguration'].portalsMapsDomainName;
        const context = new Map([
            ['proj4', proj4],
            ['mapBaseUrl', `https://${domain}/mapcache/portals/wmts/?`],
            ['wmsUrl', `https://${domain}/cgi-bin/mapserv?`]
        ]);

        const target = this.reportTarget;
        const component = new PinReport({
            target,
            props: {
                report,
                editable: report.memberId && report.memberId === this.loginService.currentMemberId(),
                reportLink: this.reportLink(report),
                ...extraProps
            },
            context
        });

        component.$on('reportUpdated', (ev) => {
            this.reportService.updateReport(ev.detail);
        });

        return {
            component,
            target
        }
    }

    showAreaReport(report: any, extraProps = {}): any {
        const target = this.reportTarget;

        const component = new AreaReport({
            props: {
                report,
                editable: report.memberId && report.memberId === this.loginService.currentMemberId(),
                reportLink: this.reportLink(report),
                ...extraProps
            },
            target
        });

        component.$on('reportUpdated', (ev) => {
            this.reportService.updateReport(ev.detail);
        });

        return {
            target,
            component
        };
    }
}
